import * as countries from 'i18n-iso-countries';
import { format } from './format-plugin';
import i18next from './i18n';

export function getCountryName(countryCode) {
  return countries.getName(countryCode, i18next.resolvedLanguage || 'en');
}

export const COUNTRY_OPTIONS = () => {
  const names = countries.getNames(i18next.resolvedLanguage || 'en', {
    select: 'official',
  });

  return Object.keys(names)
    .map((key) => ({
      label: names[key],
      value: key,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

export const TAX_ID_VALIDATIONS = {
  NL: '^(NL)?[0-9]{9}B[0-9]{2}$',
  DE: '^(DE)?[0-9]{9}$',
  BE: '^(BE)?[01][0-9]{9}$',
  LU: '^(LU)?[0-9]{8}$',
  FR: '^(FR)?[0-9A-Z]{2}[0-9]{9}$',
  ES: '^(ES)?[0-9A-Z][0-9]{7}[0-9A-Z]$',
  AT: '^(AT)?U[0-9]{8}$',
  SE: '^(SE)?[0-9]{12}$',
  DK: '^(DK)?[0-9]{8}$',
  FI: '^(FI)?[0-9]{8}$',
  GB: '^(GB)?(GD[0-9]{3}|HA[0-9]{3}|[0-9]{9}|[0-9]{12})$',
};

export const BUSINESS_ID_LABELS = {
  NL: 'KVK',
  GB: 'Company No.',
  BE: 'Ondernemingsnummer',
  DE: 'Handelsregisternummer',
  FR: 'SIRET',
  ES: 'NIF',
  AT: 'Firmenbuchnummer',
  SE: 'Organisationsnummer',
  DK: 'CVR-nummer',
  FI: 'Y-tunnus',
};

export function getBusinessIdLabel(countryCode) {
  return BUSINESS_ID_LABELS[countryCode];
}

export const TAX_ID_LABELS = {
  NL: 'BTW',
  GB: 'VAT No.',
  BE: 'BTW',
  DE: 'USt-IdNr',
  FR: 'TVA',
  ES: 'NIF-IVA',
  AT: 'UID-Nr.',
  SE: 'Momsreg.nr',
  DK: 'Momsnummer',
  FI: 'ALV-numero',
};

export function getTaxIdLabel(countryCode) {
  return TAX_ID_LABELS[countryCode];
}

export const SUPPLIER_TYPES = {
  maker: 'Maker',
  thirdparty: 'Third Party',
};
export const TAX_STATUSES = [
  { value: 'none', label: 'Taxable (NL only)' },
  { value: 'reverse', label: 'Reverse (Rest of EU)' },
  { value: 'exempt', label: 'Exempt (Non-EU)' },
];

export const LOCALES = [
  { value: 'en', label: 'English' },
  { value: 'nl', label: 'Dutch' },
];

export function getUserAttributesList(user) {
  if (!user) return null;
  const { customerOrganisation, organisation, cutlistAdminSource } = user;

  return [
    {
      label: 'Cutr ID',
      value: user.id,
    },
    {
      label: 'Email',
      value: user.email,
      badge: user.email && getEmailStatusBadge(user.emailStatus),
    },
    { label: 'Phone', value: user.phone },
    {
      label: 'Customer Organisation',
      value:
        customerOrganisation &&
        `${customerOrganisation.name || 'Unnamed Customer Organisation'}`,
      badge: customerOrganisation?.highPriority && {
        label: 'High Priority',
        variant: 'primary',
      },
      to: user.customerOrganisationId
        ? {
            name: 'organisations:details',
            params: { kind: 'customer', id: user.customerOrganisationId },
          }
        : undefined,
    },
    {
      label: 'Partner Organisation',
      value:
        organisation && (organisation.name || 'Unnamed Partner Organisation'),
      to: user.organisationId
        ? {
            name: 'organisations:details',
            params: { kind: 'partner', id: user.organisationId },
          }
        : undefined,
    },
    cutlistAdminSource && {
      label: 'Cutlist Partner',
      value: `${cutlistAdminSource.name || '-'} (${cutlistAdminSource.ticker})`,
    },
    user.stripeId && {
      label: 'Stripe ID',
      value: user.stripeId,
      to: user.stripeLink,
    },
    user.workosId && {
      label: 'WorkOS ID',
      value: user.workosId,
      to: user.workosLink,
    },
    {
      label: 'Status',
      value: user.status,
    },
    {
      label: 'Created',
      value: format.date(user.createdAt, 'medium', 'long'),
    },
    {
      label: 'Signed up',
      value: format.date(user.signedUpAt, 'medium', 'long'),
    },
    {
      label: 'Updated',
      value: format.date(user.updatedAt, 'medium', 'long'),
    },
  ].filter(Boolean);
}

const STATUS_VARIANT = {
  confirmed: 'success',
  unconfirmed: 'warning',
  supressed: 'danger',
};
function getEmailStatusBadge(status) {
  return {
    label: status,
    variant: STATUS_VARIANT[status] || 'secondary',
    class: 'capitalize',
  };
}

export function getOrderUserType(order, user) {
  if (!order || !user) return null;
  if (!('customerOrganisationId' in order)) return null;
  if (!('makerId' in order)) return null;
  if (!('type' in user)) return null;

  // eslint-disable-next-line no-restricted-properties
  if (user.type !== 'User') return user.type;

  if (!('customerOrganisationId' in user)) return null;
  if (!('organisationId' in user)) return null;

  if (
    order.customerOrganisationId &&
    order.customerOrganisationId === user.customerOrganisationId
  ) {
    return 'Customer';
  }
  if (order.makerId && order.makerId === user.organisationId) return 'Maker';

  // eslint-disable-next-line no-restricted-properties
  return user.type;
}
